import { useState, useRef, useEffect } from 'react';
import Modal from '../../../BaseComponents/Modal';
import {
  askYourResults,
  AskYourResultsResponse,
} from '../../../../api/chat';
import { toast } from 'react-toastify';
import AiMarkdownContent from '../AiMarkdownContent';
import './MemberAIChatPopup.scss';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

type MemberAIChatPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName?: string;
};

const formatBotResponse = (data: AskYourResultsResponse): string => {
  // Check for reply first, then response, then answer
  let text: string = '';
  
  if (typeof data === 'string') {
    text = data;
  } else if (data && typeof data === 'object') {
    text = data.reply ?? data.response ?? data.answer ?? '';
  }
  
  if (!text || typeof text !== 'string') {
    return '';
  }
  
  // react-markdown will safely handle the text and prevent XSS
  // Just trim whitespace
  return text.trim();
};

const MemberAIChatPopup = ({ isOpen, onClose, memberId, memberName }: MemberAIChatPopupProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachedImage, setAttachedImage] = useState<{
    fileName: string;
    mimeType: string;
    base64: string;
    previewUrl: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      setMessages([]);
      setInputValue('');
      setAttachedImage(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Failed to read file.'));
          return;
        }
        // result is a data URL: data:<mime>;base64,<bytes>
        const base64 = result.split(',', 2)[1] || '';
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file.'));
      reader.readAsDataURL(file);
    });
  };

  const handlePickImage = () => fileInputRef.current?.click();

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    // reset value so selecting the same file again triggers onChange
    e.target.value = '';
    if (!file) return;

    if (!file.type?.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }

    try {
      const base64 = await fileToBase64(file);
      const previewUrl = URL.createObjectURL(file);
      setAttachedImage({
        fileName: file.name,
        mimeType: file.type,
        base64,
        previewUrl,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to attach image.');
    }
  };

  const removeAttachedImage = () => {
    if (attachedImage?.previewUrl) {
      URL.revokeObjectURL(attachedImage.previewUrl);
    }
    setAttachedImage(null);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if ((!trimmed && !attachedImage) || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed || (attachedImage ? `[Image attached: ${attachedImage.fileName}]` : ''),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const data = await askYourResults(memberId, trimmed, attachedImage ? {
        imageBase64: attachedImage.base64,
        imageMimeType: attachedImage.mimeType,
      } : undefined);
      const responseText = formatBotResponse(data);
      
      if (!responseText) {
        // If no text found, check if success is false or if there's any error indication
        if (data.success === false) {
          throw new Error('Request was not successful');
        }
        throw new Error('No response text found in API response');
      }
      
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: responseText,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      removeAttachedImage();
    } catch (err: unknown) {
      let errorMessage = 'Something went wrong. Please try again.';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null) {
        const axiosError = err as { response?: { data?: { detail?: string; message?: string } }; message?: string };
        errorMessage = 
          axiosError?.response?.data?.detail ||
          axiosError?.response?.data?.message ||
          axiosError?.message ||
          errorMessage;
      }
      
      toast.error(errorMessage);
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          content: `Error: ${errorMessage}`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Ask about results"
        width="800px"
        memberName={memberName}
      >
        <div className="member-ai-chat-popup">
          <div className="member-ai-chat-messages">
            {messages.length === 0 && (
              <div className="member-ai-chat-placeholder">
                Ask a question about this member&apos;s results. Type your question below and
                press Send.
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`member-ai-chat-message member-ai-chat-message--${msg.role}`}
              >
                <div className="member-ai-chat-message-bubble">
                  <div className="member-ai-chat-message-content">
                    {msg.role === 'assistant' ? (
                      <AiMarkdownContent content={msg.content} />
                    ) : (
                      msg.content
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="member-ai-chat-message member-ai-chat-message--assistant">
                <div className="member-ai-chat-message-bubble member-ai-chat-message-bubble--loading">
                  <span className="member-ai-chat-typing-dots">
                    <span />
                    <span />
                    <span />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form className="member-ai-chat-form" onSubmit={handleSubmit}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageSelected}
              disabled={isLoading}
            />
            {attachedImage && (
              <div className="member-ai-chat-attachment">
                <div className="member-ai-chat-attachment-preview">
                  <img src={attachedImage.previewUrl} alt="attachment preview" />
                </div>
                <div className="member-ai-chat-attachment-meta">
                  <div className="member-ai-chat-attachment-name">{attachedImage.fileName}</div>
                  <button
                    type="button"
                    className="member-ai-chat-attachment-remove"
                    onClick={removeAttachedImage}
                    disabled={isLoading}
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
            <textarea
              ref={inputRef}
              className="member-ai-chat-input"
              placeholder="Type your question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              rows={2}
              disabled={isLoading}
            />
            <button
              type="button"
              className="member-ai-chat-attach"
              onClick={handlePickImage}
              disabled={isLoading}
              title="Attach image"
            >
              Attach
            </button>
            <button
              type="submit"
              className="member-ai-chat-send"
              disabled={(!inputValue.trim() && !attachedImage) || isLoading}
            >
              Send
            </button>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default MemberAIChatPopup;
